import React, { useState } from 'react';
import { MessageCircle, Repeat, Heart, Share, Play, Bookmark, Edit2, Save, X, ExternalLink, Tag, Star } from 'lucide-react';
import { Card, CardContent } from './card';
import ProfileImage from './ProfileImage';
import './TweetCard.css';
import SearchLink from './SearchLink';
import { useStagingTray } from '../../context/StagingTrayContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useSkipCategories } from '../../context/SkipCategoriesContext';
import FavoriteStar from './FavoriteStar';
import SkipCategoriesButton from './SkipCategoriesButton';
import SkipCategoriesModal from './SkipCategoriesModal';
import TagManager from './TagManager';
import { InkQuoteApi } from '../../services/inkquoteApi';

import ImageWithFallback from './ImageWithFallback';
import ImagePreviewModal from './ImagePreviewModal';

const TwitterIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M23.954 4.569c-.885.392-1.83.656-2.825.775 1.014-.608 1.794-1.569 2.163-2.724-.951.564-2.005.974-3.127 1.195-.897-.959-2.178-1.559-3.594-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.576-.666 2.476 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.23-.616v.062c0 2.385 1.693 4.374 3.946 4.827-.413.112-.849.171-1.296.171-.317 0-.626-.03-.927-.086.631 1.953 2.445 3.376 4.604 3.416-1.68 1.319-3.809 2.107-6.115 2.107-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.212 7.548 2.212 9.051 0 14-7.496 14-13.986 0-.21 0-.423-.015-.634.961-.695 1.8-1.562 2.46-2.549z" />
  </svg>
);

const TweetCard = ({ tweet, onUpdateTweet, onEditStateChange, viewMode = null }) => {
  const { addItem, removeItem, isInTray } = useStagingTray();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isSkipped, getSkipCategories, SKIP_CATEGORY_LABELS } = useSkipCategories();
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [isEditingAuthor, setIsEditingAuthor] = useState(false);
  const [editedText, setEditedText] = useState(tweet.text || tweet.full_text || '');
  const [editedAuthor, setEditedAuthor] = useState(tweet.user?.name || '');
  const inTray = isInTray(tweet);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const longPressTimer = React.useRef(null);
  const [isLongPressing, setIsLongPressing] = React.useState(false);
  const touchStart = React.useRef({ x: 0, y: 0, time: 0 });

  // Get skip status
  const tweetIsSkipped = isSkipped(tweet.id);
  const skipCategories = getSkipCategories(tweet.id);

  // Get tweet metadata for tags
  const metadata = tweet.metadata || {};
  const [quoteTags, setQuoteTags] = useState(metadata.tags || []);

  const isEditing = isEditingContent || isEditingAuthor;

  const isLikeFormat = tweet.source === 'like';
  const isUserTweetFormat = tweet.source === 'user_tweet';
  const tweetId = tweet.id || 'unknown';
  const tweetUrl = tweet.url || '#';
  const tweetText = tweet.text || tweet.full_text || '';
  const tweetDate = tweet.created_at || tweet.date || new Date().toISOString();
  const userName = tweet.user?.name || 'Unknown User';
  const userHandle = tweet.user?.screen_name || tweet.user?.handle || 'unknown';
  const userProfilePic = tweet.user?.profile_image_url || tweet.user?.profilePicUrl || '/default-profile-pic.png';
  const tweetMedia = tweet.medias || tweet.media || [];

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const updatedTweet = {
      ...tweet,
      text: editedText,
      full_text: editedText,
      user: {
        ...tweet.user,
        name: editedAuthor
      }
    };

    try {
      await onUpdateTweet(updatedTweet, tweet.source);
      setIsEditingContent(false);
      setIsEditingAuthor(false);
      onEditStateChange(false);
    } catch (error) {
      console.error('[TweetCard] Save failed:', error);
    }
    return false;
  };

  const handleCancel = () => {
    setEditedText(tweet.text || tweet.full_text || '');
    setEditedAuthor(tweet.user?.name || '');
    setIsEditingContent(false);
    setIsEditingAuthor(false);
    onEditStateChange(false);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderTweetText = (text) => {
    if (!text) return null;
  
    const decodedText = decodeHTMLEntities(text);
    const lines = decodedText.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Handle quote lines
      if (line.startsWith('>')) {
        const quoteContent = line.substring(1).trim();
        return (
          <blockquote 
            key={`line-${lineIndex}`}
            className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 italic text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded my-2"
          >
            {processLine(quoteContent)}
          </blockquote>
        );
      }
      
      return <div key={`line-${lineIndex}`}>{processLine(line)}</div>;
    });
  };
  
  // Helper function to process links and mentions within a line
  const processLine = (text) => {
    const regex = /(https?:\/\/[^\s]+)|(@\w+)/g;
    const parts = text.split(regex);
  
    return parts.map((part, index) => {
      if (!part) return null;
      if (part.match(/^https?:\/\//)) {
        return (
          <a
            key={`link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-twitter-blue hover:underline"
          >
            {part}
          </a>
        );
      } else if (part.startsWith('@')) {
        const username = part.slice(1);
        return (
          <SearchLink
            key={`mention-${index}`}
            operator="from"
            value={username}
            className="text-blue-500 hover:underline"
            showParens={false}
          >
            {part}
          </SearchLink>
        );
      }
      return part;
    });
  };

  const renderEditableContent = () => {
    if (isEditingContent) {
      return (
        <form
          onSubmit={handleSave}
          className="mt-2"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              handleSave(e);
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
        >
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            rows={4}
            autoFocus
          />

          {/* Dev Info */}
          {tweet._inkquote_id && (
            <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  ID: <span className="text-gray-900 dark:text-white">{tweet._inkquote_id}</span>
                </span>
                <a
                  href={`https://api.treasurechest.design/inkquote/quotes/${tweet._inkquote_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={12} />
                  API Endpoint
                </a>
              </div>
            </div>
          )}

          <div className="mt-2 flex gap-2">
            <button
              type="submit"
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
            >
              <Save size={16} /> Save
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
            >
              <X size={16} /> Cancel
            </button>
          </div>
        </form>
      );
    }

    return (
      <div
        className={`mt-2 tweet-text break-words overflow-wrap-anywhere ${viewMode !== null ? 'px-2 py-1 -mx-2 -my-1 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}`}
        onClick={viewMode !== null ? (e) => {
          e.stopPropagation();
          setIsEditingContent(true);
          onEditStateChange(true);
        } : undefined}
        title={viewMode !== null ? "Click to edit content" : undefined}
      >
        {renderTweetText(tweetText)}
      </div>
    );
  };

  const openPreview = (e, index, list) => {
    e.stopPropagation();
    setPreviewMedia({ list, index });
  };

  const renderMedia = (media) => {
    if (!media || media.length === 0) return null;

    // Build the list of previewable items in the same order they'll be rendered
    const previewList = media
      .map((item, i) => {
        if (item.type === 'photo' || item.url.match(/\.(jpeg|jpg|gif|png)$/i)) {
          return { src: item.url, alt: `Tweet media ${i + 1}`, type: 'photo' };
        } else if (item.type === 'video') {
          return { src: item.url, alt: `Video thumbnail ${i + 1}`, type: 'video', videoSrc: item.video_src, tweetUrl: item.media_url };
        }
        return null;
      })
      .filter(Boolean);

    let previewIdx = 0;
    return media.map((item, index) => {
      if (item.type === 'photo' || item.url.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const pIdx = previewIdx++;
        return (
          <div
            key={`media-${index}`}
            className="cursor-zoom-in"
            onClick={(e) => openPreview(e, pIdx, previewList)}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <ImageWithFallback
              src={item.url}
              localSrc={item.localUrl}
              alt={`Tweet media ${index + 1}`}
              className="tweet-media"
            />
          </div>
        );
      } else if (item.type === 'video') {
        const pIdx = previewIdx++;
        return (
          <div
            key={`video-${index}`}
            className="tweet-media video-container cursor-pointer"
            onClick={(e) => openPreview(e, pIdx, previewList)}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <ImageWithFallback
              src={item.url}
              localSrc={item.localUrl}
              alt={`Video thumbnail ${index + 1}`}
              className="w-full h-full"
            />
            <div className="video-overlay">
              <Play size={48} className="text-white opacity-75" />
            </div>
          </div>
        );
      }
      return null;
    });
  };

  const decodeHTMLEntities = (text) => {
    if (!text) return '';
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  const handleLongPressStart = (e) => {
    // Skip if clicking interactive elements or editing
    if (e.target.closest('a, button, form, textarea, input') || isEditing) return;

    // Record initial touch position and time
    const touch = e.touches ? e.touches[0] : e;
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    setIsLongPressing(true);
    longPressTimer.current = setTimeout(() => {
      toggleFavorite(tweetId);
      setIsLongPressing(false);
    }, 800);
  };

  const handleTouchMove = (e) => {
    // If there's an active long-press timer, check if we've moved enough to cancel it
    if (longPressTimer.current && touchStart.current) {
      const touch = e.touches ? e.touches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);

      // Cancel long-press if finger moved more than 10px (indicates scrolling)
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        setIsLongPressing(false);
      }
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsLongPressing(false);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();

    // Check if this was a scroll gesture (touch moved more than 10px or was quick)
    if (touchStart.current) {
      const touch = e.changedTouches ? e.changedTouches[0] : e;
      const deltaX = Math.abs(touch.clientX - touchStart.current.x);
      const deltaY = Math.abs(touch.clientY - touchStart.current.y);
      const touchDuration = Date.now() - touchStart.current.time;

      // Ignore if finger moved more than 10px (was scrolling)
      if (deltaX > 10 || deltaY > 10) {
        return;
      }

      // Ignore if touch was very quick (< 50ms) as it's likely scroll momentum
      if (touchDuration < 50) {
        return;
      }
    }

    toggleFavorite(tweetId);
  };

  const handleCardClick = (e) => {
    // Don't toggle if clicking on interactive elements, editing, or during long press
    if (e.target.closest('a, button, form, textarea, input') || isEditing || isLongPressing) {
      return;
    }

    // In Saves tab (viewMode=null) or Batch mode: toggle staging tray
    // In Manage mode: do nothing (editing is done via hover)
    if (viewMode === null || viewMode === 'batch') {
      if (inTray) {
        removeItem(tweet);
      } else {
        addItem(tweet);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this tweet permanently?')) {
      try {
        await InkQuoteApi.deleteTweet(tweet.id);
        window.location.reload(); // Temporary - will improve in Priority 2
      } catch (error) {
        console.error('Failed to delete tweet:', error);
        alert('Failed to delete tweet. Please try again.');
      }
    }
  };

  return (
    <Card
      key={tweetId}
      className={`tweet-card shadow-sm transition-all duration-200 ${
        viewMode === 'batch' || viewMode === null ? 'cursor-pointer' : ''
      } ${
        inTray && (viewMode === 'batch' || viewMode === null)
          ? 'ring-2 ring-blue-500 dark:ring-blue-400 !bg-blue-50 dark:!bg-blue-900/20 border-blue-300 dark:border-blue-600'
          : tweetIsSkipped
          ? 'opacity-60 bg-white dark:bg-gray-800 hover:shadow-md hover:!bg-gray-50 dark:hover:!bg-gray-700/50 border-red-200 dark:border-red-900'
          : 'bg-white dark:bg-gray-800 hover:shadow-md hover:!bg-gray-50 dark:hover:!bg-gray-700/50'
      }`}
      onClick={handleCardClick}
      onTouchStart={handleLongPressStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleLongPressEnd}
      onMouseDown={handleLongPressStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleLongPressEnd}
      onMouseLeave={handleLongPressEnd}
    >
      <CardContent className="p-4 relative">
        <div className="flex items-start mb-2">
          <ProfileImage
            src={userProfilePic}
            alt={userName}
            userHandle={userHandle}
            className="w-12 h-12 flex-shrink-0 mr-3"
          />
          <div className="flex-grow min-w-0">
            <div className="tweet-header-top flex items-start justify-between">
              <div className="tweet-user-info flex items-center gap-2">
                {isEditingAuthor ? (
                  <input
                    type="text"
                    value={editedAuthor}
                    onChange={(e) => setEditedAuthor(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        handleSave(e);
                      } else if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                    className="font-bold px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white max-w-full"
                    autoFocus
                  />
                ) : (
                  <span
                    className={`font-bold tweet-name ${viewMode !== null ? 'px-2 py-0.5 -mx-2 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''}`}
                    onClick={viewMode !== null && !isEditingContent ? (e) => {
                      e.stopPropagation();
                      setIsEditingAuthor(true);
                      onEditStateChange(true);
                    } : undefined}
                    title={viewMode !== null ? "Click to edit author" : undefined}
                  >
                    {userName}
                  </span>
                )}
                <SearchLink
                  operator="from"
                  value={userHandle}
                  className="tweet-handle"
                  showParens={false}
                >
                  @{userHandle}
                </SearchLink>
              </div>
              <div className="tweet-meta-info flex items-center gap-2">
                <span className="tweet-date">{formatDate(tweetDate)}</span>
                <FavoriteStar itemId={tweetId} size={16} inline />
              </div>
            </div>

            {/* Skip Categories Badges */}
            {tweetIsSkipped && skipCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {skipCategories.map(category => (
                  <span
                    key={category}
                    className="
                      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                      bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400
                      border border-red-200 dark:border-red-800
                    "
                  >
                    {SKIP_CATEGORY_LABELS[category]}
                  </span>
                ))}
              </div>
            )}
            {renderEditableContent()}
            {tweetMedia && tweetMedia.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {renderMedia(tweetMedia)}
              </div>
            )}
            <div className="mt-4 flex justify-between items-center tweet-actions">
              {/* Comment and Repost buttons - Only show on Saves tab */}
              {viewMode === null && (
                <>
                  <button className="tweet-action-button">
                    <MessageCircle size={18} className="mr-2" />
                    <span>{tweet.reply_count || 0}</span>
                  </button>
                  <button className="tweet-action-button">
                    <Repeat size={18} className="mr-2" />
                    <span>{tweet.retweet_count || 0}</span>
                  </button>
                </>
              )}

              <a
                href={tweetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="tweet-action-button"
              >
                <ExternalLink size={18} />
              </a>

              {/* Favorite Star Button - Only in InkQuote tab */}
              {viewMode !== null && (
                <button
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    touchStart.current = {
                      x: touch.clientX,
                      y: touch.clientY,
                      time: Date.now()
                    };
                  }}
                  onClick={handleFavoriteClick}
                  className="tweet-action-button"
                  title="Toggle Favorite"
                >
                  <Star
                    size={18}
                    className={isFavorite(tweetId) ? 'text-yellow-400 fill-yellow-400' : ''}
                  />
                </button>
              )}

              {/* Tag Button */}
              {viewMode !== null && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTagModalOpen(true);
                  }}
                  className="tweet-action-button"
                  title="Manage Tags"
                >
                  <Tag size={18} />
                </button>
              )}

              {/* Skip Button - InkQuote tab only */}
              {viewMode !== null && (
                <SkipCategoriesButton tweetId={tweetId} tweetSource={tweet.source} />
              )}

              {isLikeFormat ? (
                <Heart size={18} className="text-red-500" />
              ) : isUserTweetFormat ? (
                <TwitterIcon size={18} color="#1DA1F2" />
              ) : (
                <Bookmark size={18} className="text-blue-500" />
              )}
            </div>
          </div>
        </div>

      </CardContent>

      {/* Skip Categories Modal */}
      {isSkipModalOpen && (
        <SkipCategoriesModal
          tweetId={tweet.id}
          tweetSource={tweet.source || 'twitter'}
          isOpen={isSkipModalOpen}
          onClose={() => setIsSkipModalOpen(false)}
        />
      )}

      {/* Tag Manager Modal */}
      {isTagModalOpen && (
        <TagManager
          quoteId={tweet._inkquote_id}
          currentTags={quoteTags}
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onTagsUpdated={(newTags) => {
            setQuoteTags(newTags);
            // Optionally reload the tweet to get fresh data
            if (onUpdateTweet) {
              onUpdateTweet({ ...tweet });
            }
          }}
        />
      )}

      {/* Media preview modal */}
      {previewMedia && (
        <ImagePreviewModal
          mediaList={previewMedia.list}
          initialIndex={previewMedia.index}
          onClose={() => setPreviewMedia(null)}
        />
      )}
    </Card>
  );
};

export default TweetCard;